import { FC, useCallback, useContext, useEffect, useState } from 'react';
import { Group, Skeleton, Text } from '@mantine/core';
import { getResourcePosition, getResourcePositionFavorite } from '../../../../Service/restapi/documentService';
import { Resource } from '../../../../Types/ResourceT';
import ResourceS from '../../../../Service/ResourceS';
import { LicenceS } from '../../../../Service/LicenceS';
import ResourceWidgetItem from '../../../Resources/aside/ResourceWidgetItem';
import { ProfileContext } from '../../../../Service/Context/ProfileContext';
import { PositionDto } from '../../../../Service/DocumentT';
import ResourceAvailabilityDot from './ResourceAvailabilityDot';
import { ResourceAddonContext } from '../../../../Service/Context/ResourceAddonsContext';
import { openResourcePositionModal } from './ResourcePositionModal';
import LinkText from '../../../../Atoms/LinkText';
import { openResourcePositionCreateFromFavoriteModal } from './ResourcePositionCreateFromFavoriteModal';

export type ResourcePosition = {
  id: string;
  positionId: number;
  resourceId: string;
  exemplarId?: string;
  factorGroupId?: string;
  planningGroupTemplateId?: string;
};

type Props = {
  position: PositionDto;
  preview?: boolean;
  favoritePosition: boolean;
  eventId?: number;
  amount?: string;
  setResourcePresent?: (val: boolean) => void;
};

const ResourcePositionAssignment: FC<Props> = ({
  position,
  preview = false,
  favoritePosition,
  eventId,
  amount,
  setResourcePresent,
}) => {
  const { isActive, executeWithCheck } = useContext(ResourceAddonContext);
  const [resourcePosition, setResourcePosition] = useState<ResourcePosition | null>(null);
  const [resource, setResource] = useState<Resource | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const { profile } = useContext<any>(ProfileContext);

  const loadResourcePosition = useCallback(() => {
    setLoading(true);

    const request = favoritePosition ? getResourcePositionFavorite(position.id) : getResourcePosition(position.id);
    request
      .then((resp) => {
        if (resp.ok) {
          resp.json().then((e) => {
            setResourcePosition(e);
          });
        } else if (!favoritePosition) {
          setResourcePosition(null);
        } else {
          console.error(`could not load resource of position ${position.id}`);
        }
      })
      .catch(() => {
        console.error(`could not load resource of position ${position.id}`);
      })
      .finally(() => setLoading(false));
  }, [favoritePosition, position.id]);

  useEffect(() => {
    if (setResourcePresent) {
      if (resource) {
        setResourcePresent(true);
      } else {
        setResourcePresent(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource]);

  useEffect(() => {
    loadResourcePosition();
  }, [loadResourcePosition]);

  useEffect(() => {
    if (resourcePosition) {
      ResourceS.fetchById(resourcePosition?.resourceId)
        .then(setResource)
        .catch(() => {
          console.error(`could not load resource ${resourcePosition?.resourceId}`);
        });
    } else if (resource) {
      setResource(null);
    }
  }, [resourcePosition]);

  if (!LicenceS.hasAccessToResourceModule(profile.id)) {
    return null;
  }

  const onClick = () => {
    openResourcePositionModal({
      resourcePosition,
      setResourcePosition,
      positionId: position.id,
      resource,
      favoritePosition,
      disableSelection: !isActive,
    });
  };

  const onCreateAndLinkResource = () => {
    openResourcePositionCreateFromFavoriteModal({
      favoritePositionId: position.id,
      initialName: position.label,
      initialDescription: position.subtext ?? '',
      setResourcePosition,
    });
  };

  if (loading || (resourcePosition != null && resource == null)) {
    return <Skeleton mt="sm" mb="sm" height={20} width={150} />;
  }

  return (
    <Group position="apart">
      {resource && resourcePosition ? (
        <>
          {eventId && amount && <ResourceAvailabilityDot eventId={eventId} resourceId={resource.id} amount={amount} />}
          <ResourceWidgetItem resource={resource} onClick={onClick} />
        </>
      ) : (
        <Group spacing="md">
          <LinkText onClick={() => executeWithCheck(onClick)}>+ Ressource verknüpfen</LinkText>
          {favoritePosition && (
            <LinkText onClick={() => executeWithCheck(onCreateAndLinkResource)}>+ Ressource erstellen</LinkText>
          )}
        </Group>
      )}
    </Group>
  );
};

export default ResourcePositionAssignment;
